import { getApiBaseUrl } from '@/config/api-base-url'
import { tokenService } from '@/domain/auth/token.service'

import { type ApiErrorResponse, ApiException } from './types'

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface RequestConfig extends Omit<RequestInit, 'method' | 'body'> {
  params?: Record<string, string | number | boolean | undefined>
  // Skip the automatic access-token refresh-and-retry on 401. Set on the auth
  // endpoints themselves (login, refresh) to avoid recursion.
  skipAuthRefresh?: boolean
}

interface MutationConfig extends RequestConfig {
  body?: unknown
}

let onUnauthorized: (() => void) | null = null

export function setOnUnauthorized(callback: () => void) {
  onUnauthorized = callback
}

function buildUrl(path: string, params?: Record<string, string | number | boolean | undefined>) {
  const url = new URL(path, getApiBaseUrl())

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value))
      }
    })
  }

  return url.toString()
}

// The brand API authenticates via the X-Authorization header (the BE takes the
// last whitespace-separated segment, so the "Bearer" scheme word is accepted).
function getAuthHeaders(): Record<string, string> {
  const token = tokenService.getToken()
  return token ? { 'X-Authorization': `Bearer ${token}` } : {}
}

// ── Silent token refresh (single in-flight; see RISK-8 in the BE contract) ────
let refreshPromise: Promise<boolean> | null = null

async function exchangeRefreshToken(refreshToken: string): Promise<boolean> {
  try {
    const response = await fetch(buildUrl('/api/v1/brand/access_tokens'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Authorization': `Bearer ${refreshToken}`,
      },
    })
    if (!response.ok) return false

    // The brand token endpoints wrap their payload in a `data` envelope
    // (PR #1944 / DEV-1705): { data: { access_token } }.
    const body = (await response.json()) as { data?: { access_token?: string } }
    const accessToken = body.data?.access_token
    if (!accessToken) return false

    tokenService.setTokens({ accessToken, refreshToken })
    return true
  } catch {
    return false
  }
}

function refreshAccessToken(): Promise<boolean> {
  const refreshToken = tokenService.getRefreshToken()
  if (!refreshToken) return Promise.resolve(false)

  refreshPromise ??= exchangeRefreshToken(refreshToken).finally(() => {
    refreshPromise = null
  })
  return refreshPromise
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorData: ApiErrorResponse | null = null

    try {
      errorData = (await response.json()) as ApiErrorResponse
    } catch {
      // Response body might not be JSON
    }

    if (response.status === 401 && onUnauthorized) {
      onUnauthorized()
    }

    throw ApiException.fromErrorResponse(response.status, errorData)
  }

  if (response.status === 204) {
    return undefined as T
  }

  // Tolerate empty success bodies (e.g. accept-terms returns `head :ok`).
  try {
    return (await response.json()) as T
  } catch {
    return undefined as T
  }
}

function buildInit(method: HttpMethod, config: MutationConfig): RequestInit {
  const { body, headers = {}, ...rest } = config
  // `params` and `skipAuthRefresh` are consumed by the caller, not fetch.
  delete (rest as RequestConfig).params
  delete (rest as RequestConfig).skipAuthRefresh

  return {
    method,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
      ...(headers as Record<string, string>),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
    ...rest,
  }
}

async function send<T>(method: HttpMethod, path: string, config?: MutationConfig): Promise<T> {
  const cfg = config ?? {}
  const url = buildUrl(path, cfg.params)

  let response = await fetch(url, buildInit(method, cfg))

  // On an expired access token, refresh once and retry the request.
  if (response.status === 401 && !cfg.skipAuthRefresh) {
    const refreshed = await refreshAccessToken()
    if (refreshed) {
      response = await fetch(url, buildInit(method, cfg))
    }
  }

  return handleResponse<T>(response)
}

export const apiClient = {
  get: <T>(path: string, config?: RequestConfig) => send<T>('GET', path, config),

  post: <T>(path: string, config?: MutationConfig) => send<T>('POST', path, config),

  put: <T>(path: string, config?: MutationConfig) => send<T>('PUT', path, config),

  patch: <T>(path: string, config?: MutationConfig) => send<T>('PATCH', path, config),

  delete: <T>(path: string, config?: RequestConfig) => send<T>('DELETE', path, config),
}
